/**
 * B709 (ii) — the membership checkout (Figma 636:2): the pure half. PLAN →
 * WHO'S COVERED → PAY → DONE. The buyer names the adults (as on their ID),
 * the email the cards and receipt go to, the children in the household and
 * the add-ons, and may ask for auto-renew in the words this file writes —
 * the SAME words `membership.buy` records as the consent. PAY is the payment
 * provider's own card page (B703's gateway); the buyer comes back to
 * `/memberships?...&buy=<id>` and the door charges the saved card and mints
 * the membership. What the frame shows that the platform does not hold — a
 * second adult's own email, a resident discount line — is left out rather
 * than invented. Auto-renew starts OFF: the buyer ticks it themselves (an
 * auto-renewal is agreed to, never defaulted into).
 *
 * B709 (a3) — a GIFT (636:2's switch, 632:2's toggle; (a2)'s door): the buyer
 * names who it's for, who it's from and a note; the recipient names the
 * adults, the children and renewal when they redeem it, so the draft asks
 * none of those and the body sends nobody named and no auto-renew.
 */
import { GIFT_MESSAGE_MAX, GIFT_NAME_MAX } from "@transact/contracts";
import { formatMoney, type Money } from "./money.js";
import type { OfferPlan, OfferTier } from "./membershipsPage.js";

/** The switches a buyer reads (membership.settings.get under the guest token, B709 (i)). */
export interface BuyerSwitches {
  readonly residency: boolean;
  readonly photoId: boolean;
  readonly fmvOnReceipt: boolean;
  readonly graceDays: number;
}

export const NO_SWITCHES: BuyerSwitches = Object.freeze({ residency: false, photoId: false, fmvOnReceipt: false, graceDays: 0 });

export function buyerSwitches(res: unknown): BuyerSwitches {
  const v = (typeof res === "object" && res !== null ? res : {}) as Record<string, unknown>;
  return {
    residency: v["residency"] === true,
    photoId: v["photoId"] === true,
    fmvOnReceipt: v["fmvOnReceipt"] === true,
    graceDays: typeof v["graceDays"] === "number" && v["graceDays"] >= 0 ? v["graceDays"] : 0,
  };
}

/** B709 (a3) — who a gift is for and from; present = the gift switch is on. */
export interface GiftDraft {
  readonly recipientEmail: string;
  readonly recipientName: string;
  readonly giverName: string;
  readonly message: string;
}

export const EMPTY_GIFT: GiftDraft = Object.freeze({ recipientEmail: "", recipientName: "", giverName: "", message: "" });

export interface CheckoutDraft {
  readonly planId: string;
  readonly tierId: string;
  /** One name per named adult the tier covers, as on their ID. */
  readonly adults: readonly string[];
  readonly email: string;
  readonly children: number;
  readonly addOns: readonly string[];
  readonly zip: string;
  readonly autoRenew: boolean;
  /** B709 (a3) — on = a gift for someone else. */
  readonly gift?: GiftDraft;
}

/** B1003 — `email` is the signed-in buyer's (3.3.7: never asked blank twice). */
export function freshDraft(plan: OfferPlan, tier: OfferTier, opts: { readonly gift?: boolean; readonly email?: string } = {}): CheckoutDraft {
  return {
    planId: plan.planId,
    tierId: tier.tierId,
    adults: Array.from({ length: Math.max(1, Math.min(2, tier.covers.namedAdults)) }, () => ""),
    email: opts.email ?? "",
    children: 0,
    addOns: [],
    zip: "",
    autoRenew: false,
    ...(opts.gift === true ? { gift: EMPTY_GIFT } : {}),
  };
}

/** 636:2's line under the switch, true of the plan's span. */
export function giftNote(plan: Pick<OfferPlan, "kind">): string {
  return `Turn this on to send a voucher instead — the recipient names the adults and starts the ${plan.kind === "ANNUAL" ? "12 months" : "month"} when they redeem it.`;
}

/**
 * B828 — WHO'S COVERED's foot note: where the card is taken. Since B816 a
 * channel with a publishable key takes it on the next step of this page; only
 * a channel without one still sends the buyer to the provider's own page.
 */
export function whoCardNote(cardOnPage: boolean): string {
  return cardOnPage
    ? "Next you add your card right here; it's kept securely by the payment provider, not by us."
    : "You'll add your card on the payment provider's secure page, then come straight back here.";
}

/** B828 — the card step's note: the buyer's own case, never "if you chose". */
export type CardStepCase =
  | { readonly kind: "buy"; readonly renews: "auto" | "manual" | "gift"; readonly termWord: "year" | "month" }
  | { readonly kind: "renewal"; readonly termWord: "year" | "month" };

export function cardStepNote(c: CardStepCase): string {
  const saved = "Your card is saved securely with the payment provider and charged now";
  if (c.kind === "renewal") return `${saved} for your next ${c.termWord}. Your member number stays the same.`;
  if (c.renews === "gift") return `${saved} for the gift. It's never charged for their renewals.`;
  if (c.renews === "auto") return `${saved}. Your membership renews on this card each ${c.termWord} — we email you before it does.`;
  return `${saved}. It isn't charged again unless you renew — we'll email you before your membership ends.`;
}

/** The plan and tier a draft names, or undefined when the plan is no longer on sale. */
export function draftTier(plans: readonly OfferPlan[], draft: Pick<CheckoutDraft, "planId" | "tierId">): { readonly plan: OfferPlan; readonly tier: OfferTier } | undefined {
  const plan = plans.find((p) => p.planId === draft.planId);
  const tier = plan?.tiers.find((t) => t.tierId === draft.tierId);
  return plan !== undefined && tier !== undefined ? { plan, tier } : undefined;
}

/**
 * Which plan a tier picked on the Memberships page belongs to (the page lists
 * tiers of one term). B865: the pick is the card's `planId:tierId` — matched
 * whole, never by the tier id alone, which two plans may share.
 */
export function tierPick(plans: readonly OfferPlan[], pick: string, kind: OfferPlan["kind"]): { readonly plan: OfferPlan; readonly tier: OfferTier } | undefined {
  for (const plan of plans) {
    if (plan.kind !== kind) continue;
    const tier = plan.tiers.find((t) => `${plan.planId}:${t.tierId}` === pick);
    if (tier !== undefined) return { plan, tier };
  }
  return undefined;
}

/* ------------------------------------------------------------------ total */

export interface CheckoutQuote {
  readonly rows: readonly { readonly label: string; readonly value: string }[];
  readonly total: Money;
  readonly totalText: string;
  /** "Tax-deductible portion: …" — only when the tier states its benefits' fair-market value and the venue prints it on receipts. */
  readonly fmv?: string;
}

export function checkoutQuote(plan: OfferPlan, tier: OfferTier, draft: CheckoutDraft, switches: BuyerSwitches): CheckoutQuote {
  const picked = tier.addOns.filter((a) => draft.addOns.includes(a.addOnId));
  const amount = tier.price.amount + picked.reduce((s, a) => s + a.price.amount, 0);
  const total: Money = { amount, currency: tier.price.currency };
  const fmv = tier.fmv;
  return {
    rows: [{ label: `${tier.name} · ${plan.kind === "ANNUAL" ? "yearly" : "monthly"}`, value: formatMoney(tier.price) }, ...picked.map((a) => ({ label: a.name, value: formatMoney(a.price) }))],
    total,
    totalText: formatMoney(total),
    ...(fmv !== undefined && switches.fmvOnReceipt
      ? {
          fmv: `Tax-deductible portion: ${formatMoney({ amount: Math.max(0, amount - fmv.amount), currency: total.currency })} (the fair-market value of your benefits is ${formatMoney(fmv)}). It's on your receipt.`,
        }
      : {}),
  };
}

/* ------------------------------------------------------------------- term */

const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** The domain's own term arithmetic (members.ts addTermMonths): the same day N months on, clamped to the month's end. */
export function termEnd(start: number, kind: OfferPlan["kind"]): number {
  const d = new Date(start);
  const months = kind === "ANNUAL" ? 12 : 1;
  const y = d.getUTCFullYear() + Math.floor((d.getUTCMonth() + months) / 12);
  const m = (d.getUTCMonth() + months) % 12;
  const leap = m === 1 && ((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0);
  const day = Math.min(d.getUTCDate(), leap ? 29 : MONTH_DAYS[m]!);
  return Date.UTC(y, m, day, d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds());
}

// B659 — a day is the VENUE's day: no date is named without the venue's zone (the device's never stands in).
const longDate = (ms: number, timeZone: string): string => new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric", timeZone }).format(ms);
const shortDate = (ms: number, timeZone: string): string => new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone }).format(ms);

/** "12 months from today · Oct 9, 2026 – Oct 9, 2027" (the dates only in the venue's zone). */
export function termLine(kind: OfferPlan["kind"], now: number, timeZone: string | undefined): string {
  const span = `${kind === "ANNUAL" ? "12 months" : "1 month"} from today`;
  return timeZone === undefined ? span : `${span} · ${shortDate(now, timeZone)} – ${shortDate(termEnd(now, kind), timeZone)}`;
}

/**
 * The auto-renew sentence the buyer agrees to — what the scheduler (B703)
 * actually does: on the expiry it charges the TIER's price to the card paid
 * with today and extends from that date; reminders at 30, 7 and 1 days; a
 * failed card keeps the membership for the venue's grace days while it is
 * tried again. Turning it off is at the counter today (the portal's switch
 * is shut with that reason), so the sentence says so.
 */
export function renewalConsent(plan: OfferPlan, tier: OfferTier, now: number, switches: BuyerSwitches, timeZone: string | undefined): string {
  const span = plan.kind === "ANNUAL" ? "a year" : "a month";
  const grace =
    switches.graceDays > 0
      ? ` If the card fails, your membership stays good for ${String(switches.graceDays)} more days while we try it again.`
      : "";
  const when = timeZone === undefined ? "When your membership ends" : `On ${longDate(termEnd(now, plan.kind), timeZone)}`;
  return `${when} we'll charge ${formatMoney(tier.price)} to the card you pay with today and extend your membership ${span} from that date. We email you 30, 7 and 1 days before, and you can turn this off at any ticket window.${grace}`;
}

/* ------------------------------------------------------------ the checks */

export type CheckoutField = `adult-${string}` | "email" | "zip" | "gift-email" | "gift-name" | "gift-giver" | "gift-message";

/**
 * B1042 — the domain's MEMBER_NAME_MAX (packages/domain/src/members.ts), said
 * here too because the storefront does not depend on @transact/domain; a test
 * pins the two together. A name over it is refused at its field, by length.
 */
export const MEMBER_NAME_MAX = 80;

export function adultNameProblem(name: string): string | undefined {
  const n = name.trim().length;
  return n > MEMBER_NAME_MAX ? `This name is ${String(n)} characters — a card holds up to ${String(MEMBER_NAME_MAX)}. Shorten it.` : undefined;
}

/* B1057 — the contract's gift caps, said at their fields and counted as buyBody sends them (trimmed). */
function giftNameProblem(name: string): string | undefined {
  const n = name.trim().length;
  return n > GIFT_NAME_MAX ? `This name is ${String(n)} characters — a voucher keeps up to ${String(GIFT_NAME_MAX)}. Shorten it.` : undefined;
}

function giftNoteProblem(note: string): string | undefined {
  const n = note.trim().length;
  return n > GIFT_MESSAGE_MAX ? `This note is ${String(n)} characters — a voucher prints up to ${String(GIFT_MESSAGE_MAX)}. Shorten it.` : undefined;
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BAD_EMAIL = "That email doesn't look right — check for a missing @ or dot.";

/** Each field's own refusal, in words — checked for what it SAYS (R19), shown at the field (R18). */
export function checkoutIssues(tier: OfferTier, draft: CheckoutDraft, switches: BuyerSwitches): Partial<Record<CheckoutField, string>> {
  const out: Partial<Record<CheckoutField, string>> = {};
  if (draft.gift !== undefined) {
    // B709 (a3) — the recipient names the adults at redemption; the buyer's email takes the receipt.
    const email = draft.email.trim();
    if (email === "") out.email = "Enter the email your receipt goes to.";
    else if (!EMAIL_SHAPE.test(email)) out.email = BAD_EMAIL;
    const to = draft.gift.recipientEmail.trim();
    if (to === "") out["gift-email"] = "Enter the email the voucher goes to.";
    else if (!EMAIL_SHAPE.test(to)) out["gift-email"] = BAD_EMAIL;
    else if (to.toLowerCase() === email.toLowerCase()) out["gift-email"] = "That's your own email — turn off the gift to buy this membership for yourself.";
    const theirs = giftNameProblem(draft.gift.recipientName);
    if (theirs !== undefined) out["gift-name"] = theirs;
    const yours = giftNameProblem(draft.gift.giverName);
    if (draft.gift.giverName.trim() === "") out["gift-giver"] = "Enter your name — the voucher says who it's from.";
    else if (yours !== undefined) out["gift-giver"] = yours;
    const note = giftNoteProblem(draft.gift.message);
    if (note !== undefined) out["gift-message"] = note;
    return out;
  }
  draft.adults.forEach((name, i) => {
    const n = name.trim();
    if (n === "") out[`adult-${String(i)}`] = "Enter their full name as it's printed on their ID.";
    else if (!/\s/.test(n)) out[`adult-${String(i)}`] = "Enter a first and last name — the gate checks it against their ID.";
    else if (adultNameProblem(n) !== undefined) out[`adult-${String(i)}`] = adultNameProblem(n);
  });
  const email = draft.email.trim();
  if (email === "") out.email = "Enter the email your cards and receipt go to.";
  else if (!EMAIL_SHAPE.test(email)) out.email = BAD_EMAIL;
  const zips = tier.eligibility.residentZips;
  if (switches.residency && zips !== undefined && zips.length > 0) {
    const zip = draft.zip.trim();
    if (zip === "") out.zip = `The ${tier.name} price is for residents — enter your ZIP code.`;
    else if (residentZipMatch(zips, zip) === undefined) {
      out.zip = `The ${tier.name} price is for residents of ${residentZipsWords(zips)} — if you live elsewhere, go Back to plans and pick another tier.`;
    }
  }
  return out;
}

/*
 * B1039 — the domain's resident match (packages/domain/src/members.ts), said
 * here too because the storefront does not depend on @transact/domain: case
 * and spaces don't count, a US ZIP+4 is judged by its five-digit head, and
 * the server keeps the list's own entry. Both halves are pinned by tests.
 */
function residentZipMatch(zips: readonly string[], typed: string): string | undefined {
  const key = (z: string): string => z.replace(/\s+/gu, "").toUpperCase();
  const t = key(typed);
  if (t === "") return undefined;
  const head = /^(\d{5})-?\d{4}$/u.exec(t)?.[1];
  return zips.find((z) => key(z) === t) ?? (head === undefined ? undefined : zips.find((z) => key(z) === head));
}

/** Up to three ZIP codes, then how many more — a tier may list 500. */
function residentZipsWords(zips: readonly string[]): string {
  if (zips.length <= 3) return zips.length <= 1 ? (zips[0] ?? "") : `${zips.slice(0, -1).join(", ")} and ${zips[zips.length - 1] ?? ""}`;
  const rest = zips.length - 3;
  return `${zips.slice(0, 3).join(", ")} and ${String(rest)} other ZIP code${rest === 1 ? "" : "s"}`;
}

/** Whether this tier asks for a ZIP (the residency switch on and the tier keeps a resident list). */
export const asksZip = (tier: OfferTier, switches: BuyerSwitches): boolean => switches.residency && (tier.eligibility.residentZips?.length ?? 0) > 0;

/** The adults card's teaching line (636:2), true of the tier and the switches. */
export function adultsNote(tier: OfferTier, switches: BuyerSwitches): string {
  const who = tier.covers.namedAdults === 2 ? `${tier.name} covers two adults. Each gets their own card` : `${tier.name} covers one adult, with their own card`;
  return `${who}${switches.photoId ? "; they'll show photo ID the first time they visit" : ""}. Names can't change until renewal.`;
}

export function childrenNote(tier: OfferTier): string {
  const band = tier.covers.householdChildren.ageBand;
  return `${band !== undefined ? `Under ${String(band.max + 1)} and living` : "Living"} with you. No names needed — they come in with a named adult.`;
}

/* ---------------------------------------------------------------- the buy */

export function buyBody(draft: CheckoutDraft, quote: CheckoutQuote, consent: string, buyId: string, returnUrl: string, inPage = false): Record<string, unknown> {
  /* B816 — the page can mount the card element: ask for a setup secret, not a hosted page. */
  const page = inPage ? { inPage: true } : {};
  const g = draft.gift;
  if (g !== undefined) {
    // B709 (a3) — a gift: nobody named, no children, no auto-renew (the recipient decides at redemption).
    const name = g.recipientName.trim();
    const message = g.message.trim();
    return {
      buyId,
      planId: draft.planId,
      tierId: draft.tierId,
      namedAdults: [],
      householdCount: 0,
      addOns: [...draft.addOns],
      buyerEmail: draft.email.trim(),
      ...(draft.zip.trim() !== "" ? { zip: draft.zip.trim() } : {}),
      autoRenew: false,
      gift: { recipientEmail: g.recipientEmail.trim(), ...(name !== "" ? { recipientName: name } : {}), giverName: g.giverName.trim(), ...(message !== "" ? { message } : {}) },
      returnUrl,
      ...page,
      amount: quote.total,
    };
  }
  return {
    buyId,
    planId: draft.planId,
    tierId: draft.tierId,
    namedAdults: draft.adults.map((name) => ({ name: name.trim() })),
    householdCount: draft.children,
    addOns: [...draft.addOns],
    buyerEmail: draft.email.trim(),
    ...(draft.zip.trim() !== "" ? { zip: draft.zip.trim() } : {}),
    autoRenew: draft.autoRenew,
    ...(draft.autoRenew ? { consentText: consent } : {}),
    returnUrl,
    ...page,
    amount: quote.total,
  };
}

/** Where the card page sends the buyer back: this page, naming the purchase. */
export function buyReturnUrl(origin: string, pathname: string, tenantId: string, buyId: string): string {
  return `${origin}${pathname}?tenant=${encodeURIComponent(tenantId)}&buy=${encodeURIComponent(buyId)}`; // vocab-ok: a URL's query key, never rendered
}

export type BuyAnswer =
  | { readonly kind: "card"; readonly setupUrl?: string; readonly setupClientSecret?: string; readonly refused?: string }
  | {
      readonly kind: "bought";
      readonly memberNumber: string;
      readonly cards: readonly string[];
      readonly autoRenew: boolean;
      readonly charged?: string;
      /** B709 (a3) — a gift: the voucher and who it was sent to (no cards until it is redeemed). */
      readonly gift?: { readonly code: string; readonly to: string };
    }
  | { readonly kind: "refused"; readonly message: string };

/** membership.buy's answer, read defensively. */
export function buyAnswer(res: unknown): BuyAnswer {
  const v = (typeof res === "object" && res !== null ? res : {}) as Record<string, unknown>;
  const member = v["member"] as { memberNumber?: unknown; cards?: unknown; renewal?: { method?: unknown }; status?: unknown; email?: unknown; gift?: { voucherCode?: unknown } } | undefined;
  if (member !== undefined && typeof member.memberNumber === "string") {
    const cards = Array.isArray(member.cards) ? member.cards.filter((c): c is { holder: string; revokedAt?: number } => typeof (c as { holder?: unknown }).holder === "string" && (c as { revokedAt?: unknown }).revokedAt === undefined) : [];
    const charged = v["charged"] as Money | undefined;
    return {
      kind: "bought",
      memberNumber: member.memberNumber,
      cards: cards.map((c) => c.holder),
      autoRenew: member.renewal?.method === "AUTO",
      ...(charged !== undefined && typeof charged.amount === "number" ? { charged: formatMoney(charged) } : {}),
      ...(member.status === "GIFT_UNREDEEMED" && typeof member.gift?.voucherCode === "string" && typeof member.email === "string" ? { gift: { code: member.gift.voucherCode, to: member.email } } : {}),
    };
  }
  if (typeof v["setupUrl"] === "string" || typeof v["setupClientSecret"] === "string")
    return {
      kind: "card",
      ...(typeof v["setupUrl"] === "string" ? { setupUrl: v["setupUrl"] } : {}),
      ...(typeof v["setupClientSecret"] === "string" ? { setupClientSecret: v["setupClientSecret"] } : {}), // B816
      ...(typeof v["refused"] === "string" ? { refused: v["refused"] } : {}),
    };
  if (typeof v["refused"] === "string") return { kind: "refused", message: v["refused"] };
  return { kind: "refused", message: "The membership couldn't be bought just now. Nothing was charged — try again in a moment." };
}

/* ------------------------------------------------------- across the redirect */

/** The draft rides the card page in this tab's session storage — a per-viewer convenience, never the record. */
export const stashKey = (tenantId: string, buyId: string): string => `transact.storefront.membership-buy.${tenantId}.${buyId}`;

export function readStash(raw: string | null): CheckoutDraft | undefined {
  if (raw === null) return undefined;
  try {
    const v = JSON.parse(raw) as CheckoutDraft;
    return typeof v.planId === "string" && typeof v.tierId === "string" && Array.isArray(v.adults) && typeof v.email === "string" ? v : undefined;
  } catch {
    return undefined;
  }
}

/** A purchase id: unique per checkout, safe in a URL. */
export const newBuyId = (rand: () => number = Math.random, now: number = Date.now()): string => `mb-${now.toString(36)}-${Math.floor(rand() * 2 ** 32).toString(36)}`;
